<script setup>
import { ref, reactive, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  audienceList: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 350;

const dialogRef = ref(null);
const uiFlags = useMapGetter('audienceLists/getUIFlags');
const membersGetter = useMapGetter('audienceLists/getMembers');

const filters = reactive({ page: 1, status: 'all', query: '' });

const members = computed(() =>
  props.audienceList ? membersGetter.value(props.audienceList.id) : null
);

const isLoading = computed(() => uiFlags.value.isFetchingMembers);

const totalPages = computed(() =>
  Math.max(1, Math.ceil((members.value?.count ?? 0) / PER_PAGE))
);

const statusOptions = computed(() => [
  { value: 'all', label: t('CAMPAIGN.AUDIENCE.MEMBERS.STATUS.ALL') },
  { value: 'linked', label: t('CAMPAIGN.AUDIENCE.MEMBERS.STATUS.LINKED') },
  { value: 'invalid', label: t('CAMPAIGN.AUDIENCE.MEMBERS.STATUS.INVALID') },
]);

const fetchMembers = async () => {
  if (!props.audienceList) return;

  await store.dispatch('audienceLists/fetchMembers', {
    id: props.audienceList.id,
    page: filters.page,
    status: filters.status === 'all' ? null : filters.status,
    query: filters.query || null,
  });
};

// The status filter is a discrete choice, so it queries immediately; typing in
// the search box must not fire one request per keystroke.
watch(
  () => filters.status,
  () => {
    filters.page = 1;
    fetchMembers();
  }
);

let searchTimer = null;
const cancelPendingSearch = () => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = null;
};

watch(
  () => filters.query,
  () => {
    cancelPendingSearch();
    searchTimer = setTimeout(() => {
      filters.page = 1;
      fetchMembers();
    }, SEARCH_DEBOUNCE_MS);
  }
);

onBeforeUnmount(cancelPendingSearch);

watch(() => filters.page, fetchMembers);

const open = () => {
  cancelPendingSearch();
  filters.page = 1;
  filters.status = 'all';
  filters.query = '';
  fetchMembers();
  dialogRef.value?.open();
};

const goToPage = page => {
  if (page < 1 || page > totalPages.value) return;
  filters.page = page;
};

defineExpose({ dialogRef, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="4xl"
    overflow-y-auto
    :title="t('CAMPAIGN.AUDIENCE.MEMBERS.TITLE', { name: audienceList?.name })"
    :show-confirm-button="false"
    :cancel-button-label="t('CAMPAIGN.AUDIENCE.MEMBERS.CLOSE')"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
        <!-- Both controls are placeholder-only by design (the dialog title
             already frames them), so they carry explicit aria-labels rather
             than visible ones. -->
        <Input
          v-model="filters.query"
          class="flex-1"
          :placeholder="t('CAMPAIGN.AUDIENCE.MEMBERS.SEARCH_PLACEHOLDER')"
          :aria-label="t('CAMPAIGN.AUDIENCE.MEMBERS.SEARCH_PLACEHOLDER')"
        />
        <ComboBox
          v-model="filters.status"
          :options="statusOptions"
          :aria-label="t('CAMPAIGN.AUDIENCE.MEMBERS_FILTER.STATUS_LABEL')"
          class="sm:w-48 [&>div>button]:bg-n-alpha-black2"
        />
      </div>

      <div
        v-if="isLoading"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <div
        v-else-if="!members?.payload?.length"
        class="py-10 text-sm text-center text-n-slate-11"
      >
        {{ t('CAMPAIGN.AUDIENCE.MEMBERS.EMPTY') }}
      </div>

      <!-- The table scrolls inside its own box so the dialog never scrolls
           sideways on a phone. -->
      <div v-else class="overflow-x-auto border rounded-lg border-n-weak">
        <table class="w-full min-w-[44rem] text-sm text-left">
          <thead class="bg-n-alpha-1 text-n-slate-11">
            <tr>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.AUDIENCE.MEMBERS.TABLE.NAME') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.AUDIENCE.MEMBERS.TABLE.PHONE') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.AUDIENCE.MEMBERS.TABLE.EMAIL') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.AUDIENCE.MEMBERS.TABLE.STATUS') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.AUDIENCE.MEMBERS.TABLE.SOURCE') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('CAMPAIGN.AUDIENCE.MEMBERS.TABLE.ATTRIBUTES') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="member in members.payload"
              :key="member.id"
              class="border-t border-n-weak text-n-slate-12"
            >
              <td class="px-3 py-2">{{ member.name || '—' }}</td>
              <td class="px-3 py-2 whitespace-nowrap">
                {{ member.phone_number || '—' }}
              </td>
              <td class="px-3 py-2">{{ member.email || '—' }}</td>
              <td class="px-3 py-2 whitespace-nowrap">
                <span
                  class="px-2 py-0.5 text-xs rounded-md"
                  :class="
                    member.status === 'linked'
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-ruby-3 text-n-ruby-11'
                  "
                >
                  {{
                    member.status === 'linked'
                      ? t('CAMPAIGN.AUDIENCE.MEMBERS.STATUS.LINKED')
                      : member.invalid_reason ||
                        t('CAMPAIGN.AUDIENCE.MEMBERS.STATUS.INVALID')
                  }}
                </span>
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                <span
                  v-if="member.source_kind === 'interview_candidates'"
                  class="px-2 py-0.5 text-xs rounded-md bg-n-blue-3 text-n-blue-11"
                >
                  {{ t('CAMPAIGN.AUDIENCE.MEMBERS.SOURCE.CANDIDATE') }}
                </span>
                <span v-else class="text-xs text-n-slate-10">
                  {{ t('CAMPAIGN.AUDIENCE.MEMBERS.SOURCE.IMPORT') }}
                </span>
              </td>
              <td class="px-3 py-2 text-xs text-n-slate-11">
                <span
                  v-for="(value, key) in member.custom_attributes"
                  :key="key"
                  class="inline-block px-2 py-0.5 mb-1 mr-1 rounded-md bg-n-alpha-2"
                >
                  {{ `${key}: ${value}` }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="members?.count"
        class="flex items-center justify-between text-xs text-n-slate-11"
      >
        <span>
          {{
            t('CAMPAIGN.AUDIENCE.MEMBERS.PAGINATION', {
              page: filters.page,
              total: totalPages,
              count: members.count,
            })
          }}
        </span>
        <div class="flex items-center gap-2">
          <Button
            variant="faded"
            color="slate"
            size="xs"
            icon="i-lucide-chevron-left"
            :disabled="filters.page <= 1"
            :aria-label="t('CAMPAIGN.AUDIENCE.MEMBERS.PREVIOUS')"
            @click="goToPage(filters.page - 1)"
          />
          <Button
            variant="faded"
            color="slate"
            size="xs"
            icon="i-lucide-chevron-right"
            :disabled="filters.page >= totalPages"
            :aria-label="t('CAMPAIGN.AUDIENCE.MEMBERS.NEXT')"
            @click="goToPage(filters.page + 1)"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
